import type {ChangeEvent, Dispatch, SetStateAction} from 'react';
import {useCallback, useState} from 'react';

import {createComment, finishIssue, setIssueOpen} from '../../utils/githubHelpers';

interface useAnswerProps {
    issueNumber: number;
    setUpdated: Dispatch<SetStateAction<number>>;
}

/**
 * Use new issue form.
 *
 * @param props             The props.
 * @param props.setUpdated  The set updated.
 * @param props.issueNumber The issue ID.
 * @returns The new issue form handler.
 */
export const useAnswer = ({issueNumber, setUpdated}: useAnswerProps) => {
    const [answer, setAnswer] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    /**
     * Handle body.
     *
     * @param event The event.
     */
    const handleComment = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
        setAnswer(event.target.value);
    }, [setAnswer]);

    const handleNewComment = useCallback(async () => {
        const comment = await createComment(issueNumber, answer);
        const issue = await setIssueOpen(issueNumber);

        if (comment !== null && issue !== null) {
            setAnswer('');
            setUpdated(Date.now());

            return;
        }

        setError('Something went wrong. Please try again.');
    }, [answer, issueNumber, setUpdated]);

    const handleFinishedIssue = useCallback(async () => {
        const issue = await finishIssue(issueNumber);

        if (issue !== null) {
            setUpdated(Date.now());

            return;
        }

        setError('Something went wrong. Please try again.');
    }, [issueNumber, setUpdated]);

    return {
        answer,
        error,
        handleComment,
        handleFinishedIssue,
        handleNewComment
    };
};